/**
 * Demo mode — point the app at the shared demo dataset instead of the signed-in
 * user's own organisation.
 *
 * ## What it changes
 *
 * 1. **NATS identity.** The connection is re-established with the same Keycloak
 *    JWT but a NATS `user` of `demo`, so the server-side auth callout issues
 *    demo-scoped permissions rather than the organisation's.
 * 2. **Org alias.** Everything subject- and stream-named is derived from the
 *    alias `demo` instead of the token's `organization` claim, which yields
 *    `demo.data.request` and the `DEMO_LIVE` / `DEMO_CURRENT` / `DEMO_SHIFT`
 *    streams through the existing `getOrgStreamName` derivation.
 * 3. **Chat API.** Every cockpit request carries `X-Enlixy-Demo: true`.
 * 4. **Per-user features off.** Navigation history, targets, the What's New
 *    briefing, chart comments and flow-layout editing all read or write
 *    per-user state keyed to the real account. In a demo that state is both
 *    meaningless and a leak risk, and the demo NATS account may not even be
 *    granted the subjects, so each is disabled at its source rather than left
 *    to fail noisily.
 *
 * ## Why toggling reloads the page
 *
 * The alias is baked into live JetStream consumers, three kinds of Web Worker
 * (each holding its own NATS socket), cached stream names in hook state, and a
 * SAP machine list cached for an hour. Swapping all of that in place would mean
 * coordinating a teardown across every one of those owners, and anything missed
 * would leave the *real* organisation's data on screen in a demo session — the
 * worst possible failure for this feature. A reload re-derives all of it from
 * the persisted flag, with no stale state by construction.
 */

import { useSettingsStore } from '../store/SettingsStore';
import { getOrgAlias, getOrganizationFromToken } from '../utils/tokenUtils';

/**
 * Organisation alias used for every demo subject and stream name.
 * `getOrgStreamName('demo')` → `DEMO`, so the streams become `DEMO_LIVE`,
 * `DEMO_CURRENT` and `DEMO_SHIFT`, and requests go to `demo.data.request`.
 */
export const DEMO_ORG_ALIAS = 'demo';

/** NATS `user` presented alongside the JWT when demo mode is on. */
export const DEMO_NATS_USER = 'demo';

/** Header the cockpit API uses to serve demo responses. */
export const DEMO_HEADER = 'X-Enlixy-Demo';

/**
 * Why demo mode turned itself on, used to word the badge/tooltip for the
 * automatic case. `null` means "not automatic" (either real data, or the user
 * flipped the manual switch).
 */
export type AutoDemoReason = 'no-org' | 'no-workspace' | 'no-machines';

/**
 * sessionStorage marker recording that the user turned demo OFF this session.
 * It must survive the reload `setDemoModeAndReload` performs so the automatic
 * path (which lives in non-persisted store state and is wiped by the reload)
 * does not immediately turn demo back on for a no-workspace user. Scoped to the
 * tab/session so a fresh session re-evaluates automatic demo from scratch.
 */
export const DEMO_AUTO_OPTOUT_KEY = 'demo-auto-optout';

/**
 * Whether the token proves there is no real backend to talk to, so the app
 * should present the shared demo dataset instead of a failure screen. This is
 * the single synchronous predicate for triggers 1 (no organisation) and 2
 * (organisation without a workspace) — kept here as one documented decision
 * rather than scattered inline checks at the call sites.
 *
 * `getOrganizationFromToken` already returns `null` for a missing/empty
 * `organization` claim (trigger 1). For trigger 2 we treat a false
 * `hasWorkspace` OR an empty `databaseName` as "no workspace": `hasWorkspace`
 * is the boolean the token carries this signal in, and an empty `databaseName`
 * is the concrete tell that nothing is addressable in NATS/Doris. The decode is
 * pure and synchronous, so a caller can set the automatic flag before the very
 * first connect without awaiting anything.
 */
export function shouldAutoDemoFromToken(token: string): boolean {
  return autoDemoReasonFromToken(token) !== null;
}

/**
 * The specific reason the token warrants automatic demo, or `null` when the
 * account has a usable workspace. Callers pass this to `enableAutoDemo` so the
 * badge can explain *why* demo is active.
 */
export function autoDemoReasonFromToken(token: string): Exclude<AutoDemoReason, 'no-machines'> | null {
  const org = getOrganizationFromToken(token);
  if (org === null) return 'no-org';
  if (org.hasWorkspace === false || !org.databaseName) return 'no-workspace';
  return null;
}

/**
 * Whether demo mode is active — the EFFECTIVE value, combining the manual
 * (persisted) switch with the automatic (session-scoped) fallback. Every
 * consumer already calls through here, so flipping either flag routes the whole
 * app at the demo dataset with no new call sites.
 *
 * Safe to call from non-React code — it reads the store's vanilla snapshot, so
 * services and workers' owners see the same value as the UI.
 */
export function isDemoMode(): boolean {
  const s = useSettingsStore.getState();
  return s.demoMode || s.autoDemoMode;
}

/**
 * The org alias to build subjects and stream names from.
 *
 * Use this anywhere `getOrgAlias` would otherwise be called for NATS
 * addressing; `getOrgAlias` itself stays pure so token inspection (licences,
 * org admin, database name) is unaffected by demo mode.
 */
export function getActiveOrgAlias(token: string): string | null {
  if (isDemoMode()) return DEMO_ORG_ALIAS;
  return getOrgAlias(token);
}

/**
 * NATS `user` for the current mode, or `undefined` to connect with the JWT
 * alone (the normal case).
 */
export function getActiveNatsUser(): string | undefined {
  return isDemoMode() ? DEMO_NATS_USER : undefined;
}

/**
 * Flip demo mode and restart the app.
 *
 * Persisting before reloading is what makes this work: the flag is read back
 * during module initialisation on the next load, before anything connects.
 */
export function setDemoModeAndReload(enabled: boolean): void {
  useSettingsStore.getState().setDemoMode(enabled);
  try {
    // Turning demo OFF records a session opt-out that survives the reload, so
    // the automatic fallback does not re-enable it (a no-workspace user who
    // chose "off" must see their real empty/unavailable screen). Turning it ON
    // clears the marker so automatic demo can work again in a later session.
    if (enabled) {
      sessionStorage.removeItem(DEMO_AUTO_OPTOUT_KEY);
    } else {
      sessionStorage.setItem(DEMO_AUTO_OPTOUT_KEY, '1');
    }
  } catch {
    /* storage blocked — opt-out is best-effort */
  }
  // Drop the once-per-session prewarm marker so the new org gets prewarmed.
  try {
    sessionStorage.removeItem('nats-prewarmed');
  } catch {
    /* storage blocked — prewarm is best-effort anyway */
  }
  window.location.reload();
}
